import {
  EntitlementError,
  IdentityUsedError,
  InvalidCredentialsError,
  InvalidInputError,
  NotFoundError,
  UnknownApiError,
  UnauthorizedError,
  EntitlementPinCodeIncorrect,
  EntitlementPinCodeNotSubmitted,
} from './errors';
import {
  assertAuthData,
  assertString,
  assertRequiredString,
  assertBoolean,
} from './utils';

function parseEntitlementErrors(body) {
  if (body.error) {
    return new EntitlementError(body.error.message, [body.error]);
  }

  if (body.errors.length !== 1) {
    return new EntitlementError(body.message, body.errors);
  }

  switch (body.errors[0].reason) {
    case 'PINCODE_INCORRECT': {
      return new EntitlementPinCodeIncorrect(body.message);
    }
    case 'PINCODE_NOT_SUBMITTED': {
      return new EntitlementPinCodeNotSubmitted(body.message);
    }
    default: {
      return new EntitlementError(body.message, body.errors);
    }
  }
}

export default function (api) {
  /**
   * @typedef {Object} AuthResponse
   * @property {string} token - Session token.
   * @property {string} userId - User ID.
   * @property {string} email - Login email
   * @property {string} openId - User OpenID.
   */

  /**
   * @typedef {Object} EntitlementResponse
   * @property {string} token
   */

  /**
   * @memberof API
   * @instance
   */
  const auth = {
    /**
     * Basic Authenticate with username and password.
     *
     * @method
     *
     * @param {Object} payload Key/value map of payload for the request.
     * @param {string} payload.username
     * @param {string} payload.password
     *
     * @return {Promise} Authentication result.
     * @fulfill {AuthResponse} response data.
     * @reject {ArgumentError}
     * @reject {InvalidInputError} Payload cannot pass backend validation.
     * @reject {InvalidCredentialsError} Credentials in payload cannot be authorized.
     * @reject {NotFoundError} User not found
     *
     * @example
     * api.auth.basic({
     *   username: 'username',
     *   password: 'password',
     * }).then((data) => {
     *   const {
     *     userId,
     *     token,
     *   } = data;
     * }, (err) => {
     *   if (err instanceof InvalidCredentialsError) {
     *     ...
     *   }
     * });
     */
    async basic(payload = {}) {
      assertRequiredString(payload.username, 'payload.username');
      assertRequiredString(payload.password, 'payload.password');

      const fieldMapping = (origin) => {
        switch (origin) {
          case 'identity':
            return 'username';
          case 'accessKey':
            return 'password';
          default:
            return origin;
        }
      };

      const res = await api.request('post', '/login/v2/auth/magine', {
        data: {
          identity: payload.username,
          accessKey: payload.password,
        },
      });

      switch (res.status) {
        case 200: {
          const {
            userId,
            token,
          } = res.body;
          api.setAuthData({ userId, token });
          return res.body;
        }
        case 400: {
          const fields = res.body.errors.reduce((acc, error) => ({
            ...acc,
            [fieldMapping(error.field)]: error.reason,
          }), {});
          throw new InvalidInputError(res.body.message, fields);
        }
        case 401: {
          throw new InvalidCredentialsError(res.body.message);
        }
        case 404: {
          throw new NotFoundError('User not found');
        }
        default: {
          throw new UnknownApiError(res.body.message, res);
        }
      }
    },

    /**
     * After retrieving OAuth token from some provider call this API to authenticate
     * in backend with the token.
     *
     * @method
     *
     * @param {Object} payload Key/value map of payload for the request.
     * @param {string} payload.provider
     * @param {string} payload.identity
     * @param {string} payload.accessKey
     *
     * @return {Promise} Authentication result.
     * @fulfill {AuthResponse} response data.
     * @reject {ArgumentError}
     * @reject {InvalidInputError} Payload cannot pass backend validation.
     * @reject {InvalidCredentialsError} Credentials in payload cannot be authorized.
     * @reject {NotFoundError} User not found
     *
     * @example
     * api.auth.oauth({
     *   provider: 'google',
     *   identity: 'google-oauth-id',
     *   accessKey: 'google-oauth-access-token',
     * }).then((data) => {
     *   const {
     *     userId,
     *     token,
     *   } = data;
     * });
     */
    async oauth(payload = {}) {
      assertRequiredString(payload.provider, 'payload.provider');
      assertRequiredString(payload.identity, 'payload.identity');
      assertRequiredString(payload.accessKey, 'payload.accessKey');

      const res = await api.request('post', `/login/v2/auth/${payload.provider}`, {
        data: {
          identity: payload.identity,
          accessKey: payload.accessKey,
        },
      });

      switch (res.status) {
        case 200: {
          const {
            userId,
            token,
          } = res.body;
          api.setAuthData({ userId, token });
          return res.body;
        }
        case 400: {
          throw new InvalidInputError(res.body.message);
        }
        case 401: {
          throw new InvalidCredentialsError(res.body.message);
        }
        case 404: {
          throw new NotFoundError('User not found');
        }
        default: {
          throw new UnknownApiError(res.body.message, res);
        }
      }
    },

    /**
     * Request password reset email.
     *
     * @method
     *
     * @param {Object} payload Key/value map of payload for the request.
     * @param {string} payload.email
     * @param {string} payload.locale
     *
     * @return {Promise}
     * @fulfill {undefined}
     * @reject {ArgumentError}
     * @reject {InvalidInputError} Payload cannot pass backend validation.
     */
    async requestPasswordReset(payload = {}) {
      assertRequiredString(payload.email, 'payload.email');
      assertRequiredString(payload.locale, 'payload.locale');

      const res = await api.request('post', '/login/v2/password/send-reset-email', {
        data: {
          email: payload.email,
          locale: payload.locale,
        },
      });

      switch (res.status) {
        case 202: {
          return;
        }
        case 400: {
          throw new InvalidInputError(res.body.message);
        }
        case 404: {
          throw new InvalidInputError('email is not registered');
        }
        default: {
          throw new UnknownApiError(res.body.message, res);
        }
      }
    },

    /**
     * Reset password.
     *
     * @method
     *
     * @param {Object} payload Key/value map of payload for the request.
     * @param {string} payload.token
     * @param {string} payload.password
     *
     * @return {Promise}
     * @fulfill {undefined}
     * @reject {ArgumentError}
     * @reject {InvalidInputError}
     */
    async resetPassword(payload = {}) {
      assertRequiredString(payload.token, 'payload.token');
      assertRequiredString(payload.password, 'payload.password');

      const res = await api.request('put', '/login/v1/password/reset', {
        data: {
          token: payload.token,
          password: payload.password,
        },
      });

      switch (res.status) {
        case 200: {
          return;
        }
        case 404: {
          throw new InvalidInputError('invalid token');
        }
        default: {
          throw new UnknownApiError(res.body.message, res);
        }
      }
    },

    /**
     * Create a new user using email as the id for the account.
     *
     * @method
     *
     * @param {Object} payload Key/value map of payload for the request.
     * @param {string} payload.identity must be an email address. Will be used
     * as an identity for the account
     * @param {string} payload.password a secret passphrase
     * @param {string} payload.email a contact email, could be different from identity
     * @param {string} payload.locale a 2 letter language code, for the language
     * prefered by the user
     * @param {string} payload.name the name of the user creating an account
     * @param {string=} payload.country a 2 letter country code, determining the
     * market the user account will be associated with
     * @param {boolean=} payload.emailOptIn whether or not the user has chosen to
     * receive the newsletter
     * @param {string=} payload.promotionCode
     *
     * @return {Promise}
     * @fulfill {AuthResponse}
     * @reject {ArgumentError}
     * @reject {InvalidInputError}
     * @reject {IdentityUsedError}
     */
    async basicSignup(payload = {}) {
      assertRequiredString(payload.identity, 'payload.identity');
      assertRequiredString(payload.password, 'payload.password');
      assertRequiredString(payload.email, 'payload.email');
      assertRequiredString(payload.locale, 'payload.locale');
      assertRequiredString(payload.name, 'payload.name');
      assertString(payload.country, 'payload.country');
      assertBoolean(payload.emailOptIn, 'payload.emailOptIn');
      assertString(payload.promotionCode, 'payload.promotionCode');

      const fieldMapping = (origin) => {
        switch (origin) {
          case 'accessKey':
            return 'password';
          default:
            return origin;
        }
      };

      const {
        identity,
        password,
        email,
        locale,
        name,
        country,
        emailOptIn,
        promotionCode,

        ...rest
      } = payload;

      // `/magine` is equivalent to `/email` in the API
      const res = await api.request('put', '/login/v2/auth/magine', {
        data: {
          ...rest,
          identity,
          accessKey: password,
          email,
          country,
          locale,
          name,
          emailOptIn,
          promotionCode,
        },
      });

      switch (res.status) {
        case 201: {
          return res.body;
        }
        case 400: {
          const fields = res.body.errors.reduce((acc, error) => ({
            ...acc,
            [fieldMapping(error.field)]: error.reason,
          }), {});
          throw new InvalidInputError(res.body.message, fields);
        }
        case 409: {
          throw new IdentityUsedError();
        }
        default: {
          throw new UnknownApiError(res.body.message, res);
        }
      }
    },

    /**
     * OAuth signup.
     *
     * @method
     *
     * @param {Object} payload Key/value map of payload for the request
     * @param {string} payload.provider
     * @param {string} payload.identity must be an email address
     * @param {string} payload.accessKey access token from OAuth provider
     * @param {string} payload.name
     * @param {string=} payload.email
     * @param {string=} payload.country country code
     * @param {string=} payload.locale locale code
     * @param {boolean=} payload.emailOptIn
     * @param {string=} payload.promotionCode
     *
     * @return {Promise}
     * @fulfill {AuthResponse}
     * @reject {ArgumentError}
     * @reject {InvalidInputError}
     */
    async oauthSignup(payload = {}) {
      assertRequiredString(payload.provider, 'payload.provider');
      assertRequiredString(payload.identity, 'payload.identity');
      assertRequiredString(payload.accessKey, 'payload.accessKey');
      assertRequiredString(payload.name, 'payload.name');
      assertString(payload.email, 'payload.email');
      assertString(payload.country, 'payload.country');
      assertString(payload.locale, 'payload.locale');
      assertString(payload.emailOptIn, 'payload.emailOptIn');
      assertString(payload.promotionCode, 'payload.promotionCode');

      const {
        provider,
        identity,
        accessKey,
        name,
        email,
        country,
        locale,
        emailOptIn,
        promotionCode,
      } = payload;

      const res = await api.request('put', `/login/v2/auth/${provider}`, {
        data: {
          identity,
          accessKey,
          name,
          email,
          country,
          locale,
          emailOptIn,
          promotionCode,
        },
      });

      switch (res.status) {
        case 200:
        case 201: {
          return res.body;
        }
        case 400: {
          throw new InvalidInputError(res.body.message);
        }
        default: {
          throw new UnknownApiError(res.body.message, res);
        }
      }
    },

    /**
     * Get the entitlement token for a viewable asset
     * Auth token is required.
     *
     * @method
     *
     * @param {Object} payload
     * @param {string} payload.assetId
     * @param {string} payload.pinCode
     *
     * @return {Promise} promise
     * @fulfill {EntitlementResponse}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError} Asset ID is required
     * @reject {UnauthorizedError} Unauthorized error
     * @reject {EntitlementError} Entitlement error
     * @reject {UnknownApiError} Server returned with unexpected status code
    */
    async entitlementToken(payload = {}) {
      assertAuthData(api.authData);
      assertRequiredString(payload.assetId, 'payload.assetId');
      assertString(payload.pinCode, 'payload.pinCode');

      const res = await api.request('post', `/entitlement/v1/asset/${payload.assetId}`, {
        data: {
          pinCode: payload.pinCode,
        },
      });

      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 401: {
          throw new UnauthorizedError(res.body.message);
        }
        case 403: {
          throw parseEntitlementErrors(res.body);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },
  };

  return auth;
}
